import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent, pickLocalized, resolveAssetUrl } from "@/contexts/SiteContentContext";
import { SERVICE_DETAILS } from "@/lib/serviceDetails";
import * as Lucide from "lucide-react";

const MotionLink = motion.create(Link);

const resolveIcon = (name) => {
  if (!name) return Lucide.Sparkles;
  return Lucide[name] || Lucide.Sparkles;
};

const ServiceCard = ({ s, locale, t }) => {
  const Icon = resolveIcon(s.icon);
  const title = pickLocalized(s.title, locale);
  const desc = pickLocalized(s.desc, locale);
  const image = resolveAssetUrl(s.image_url);
  const hasDetail = !!SERVICE_DETAILS[s.key];
  const Component = hasDetail ? MotionLink : motion.a;
  const linkProps = hasDetail ? { to: `/services/${s.key}` } : { href: "#contact" };

  if (s.dark) {
    return (
      <Component
        {...linkProps}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        data-testid={`service-card-${s.key}`}
        className="group relative overflow-hidden rounded-3xl bg-neutral-950 text-white p-8 lg:p-10 lg:col-span-2 lg:row-span-2"
      >
        {image && (
          <div className="absolute inset-0 opacity-50 group-hover:opacity-60 transition-opacity">
            <img src={image} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-neutral-950 via-neutral-950/85 to-transparent" />
          </div>
        )}
        <div className="relative flex flex-col h-full min-h-[280px] lg:min-h-[360px] justify-between">
          <div>
            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur grid place-items-center border border-white/10">
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="font-display mt-6 text-2xl lg:text-4xl font-light tracking-tight max-w-md">
              {title}
            </h3>
            <p className="mt-4 text-neutral-300 leading-relaxed max-w-md">{desc}</p>
          </div>
          <div className="flex items-center justify-between mt-6">
            <span className="text-xs uppercase tracking-[0.22em] text-sky-300">
              {t("services.eyebrow")}
            </span>
            <span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center group-hover:bg-white group-hover:text-neutral-950 transition-colors">
              <Lucide.ArrowUpRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Component>
    );
  }

  return (
    <Component
      {...linkProps}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid={`service-card-${s.key}`}
      className="group relative overflow-hidden rounded-3xl bg-white border border-neutral-200 p-7 lg:p-8 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(0,0,0,0.08)] transition-all"
    >
      {image && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <img src={image} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-white via-white/95 to-white/80" />
        </div>
      )}
      <div className="relative flex flex-col h-full min-h-[260px]">
        <div className="w-12 h-12 rounded-2xl bg-neutral-950 text-white grid place-items-center">
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-display mt-5 text-xl lg:text-2xl font-medium text-neutral-950 tracking-tight">
          {title}
        </h3>
        <p className="mt-3 text-neutral-600 text-[15px] leading-relaxed">{desc}</p>
        <div className="mt-auto pt-6 flex items-center text-sm text-sky-700 font-medium">
          <span>Mehr</span>
          <Lucide.ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </Component>
  );
};

const Services = () => {
  const { t, locale } = useLanguage();
  const { content } = useSiteContent();
  const services = content?.services || [];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="container-px mx-auto max-w-7xl py-24 lg:py-32"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
            {t("services.eyebrow")}
          </p>
          <h2 className="font-display mt-3 text-4xl lg:text-6xl font-light tracking-tighter text-neutral-950 text-balance">
            {t("services.title")}
          </h2>
        </div>
        <p className="text-neutral-600 max-w-md lg:text-right">{t("services.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 auto-rows-[1fr]">
        {services.map((s, i) => (
          <ServiceCard key={s.key || i} s={s} locale={locale} t={t} />
        ))}
      </div>
    </section>
  );
};

export default Services;
